/**
 * KRA Photo Journal – LazyImage
 *
 * Lazy-loaded image with:
 * - native loading="lazy"
 * - fade-in animation once loaded
 * - skeleton placeholder while loading
 * - graceful fallback on error
 */

import React, { useState, useCallback } from "react";
import { cn } from "../lib/utils.js";

interface LazyImageProps {
  src: string;
  alt: string;
  className?: string;
  aspectRatio?: string; // e.g. "3/4" for portrait
  objectFit?: "cover" | "contain";
}

export function LazyImage({
  src,
  alt,
  className,
  aspectRatio = "3/4",
  objectFit = "cover",
}: LazyImageProps) {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  const handleLoad = useCallback(() => setLoaded(true), []);
  const handleError = useCallback(() => setError(true), []);

  return (
    <div
      className={cn("relative overflow-hidden bg-surface-2", className)}
      style={{ aspectRatio }}
    >
      {/* Skeleton shimmer */}
      {!loaded && !error && (
        <div className="absolute inset-0 animate-pulse bg-gradient-to-r from-surface-2 via-surface-1 to-surface-2 bg-[length:200%_100%]" />
      )}

      {/* Error state */}
      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-surface-2">
          <svg
            className="w-8 h-8 text-ink-3"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5M12 3v2.25m0 0V21m0-18.75V3"
            />
          </svg>
        </div>
      )}

      {/* Actual image */}
      {!error && (
        <img
          src={src}
          alt={alt}
          loading="lazy"
          decoding="async"
          onLoad={handleLoad}
          onError={handleError}
          className={cn(
            "absolute inset-0 w-full h-full transition-opacity duration-300",
            objectFit === "cover" ? "object-cover" : "object-contain",
            loaded ? "opacity-100" : "opacity-0"
          )}
        />
      )}
    </div>
  );
}
